import React, { useState, useEffect } from 'react';
import { Download, X, Share, Smartphone, Laptop, Check } from 'lucide-react';
import { isAppMode } from '@/utils/pwa-utils';

const PWAInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(() => {
    return typeof window !== 'undefined' ? window.deferredPWAInstallPrompt : null;
  });
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Si ya es app nativa o PWA instalada, no mostrar nunca
    if (isAppMode()) return;

    // Detectar iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const iosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(iosDevice);

    // Si ya cerró el banner en esta sesión, no insistir de inmediato (soporta ?pwa=true para pruebas)
    const forceShow = typeof window !== 'undefined' && window.location.search.includes('pwa=true');
    const dismissed = sessionStorage.getItem('pwa_banner_dismissed_session');
    if (dismissed && !forceShow) return;

    // Escuchar beforeinstallprompt si se dispara después
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPWAInstallPrompt = e;
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Mostrar el banner tras 800ms de carga para entrada suave
    const timer = setTimeout(() => {
      setShowBanner(true);
    }, 800);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    const promptEvent = deferredPrompt || (typeof window !== 'undefined' ? window.deferredPWAInstallPrompt : null);

    if (promptEvent) {
      promptEvent.prompt();
      const { outcome } = await promptEvent.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem('pwa_installed_success', 'true');
        setShowBanner(false);
      }
      setDeferredPrompt(null);
      if (typeof window !== 'undefined') window.deferredPWAInstallPrompt = null;
      return;
    }

    // Si no hay prompt nativo disponible (iOS Safari o navegadores de escritorio que requieren añadir manual)
    setShowInstructions(true);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('pwa_banner_dismissed_session', 'true');
  };

  if (!showBanner || isAppMode()) return null;

  return (
    <>
      {/* Banner Flotante Responsivo (móvil: barra inferior centrada; desktop: toast elegante en esquina) */}
      <div className="fixed bottom-4 sm:bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 z-50 animate-spring-in">
        <div className="bg-stone-900/95 backdrop-blur-xl text-white p-3.5 sm:p-4 rounded-2xl shadow-2xl border border-stone-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0 shadow-md shadow-indigo-600/30">
              <img 
                src="/icon-192x192.png" 
                alt="PGastos" 
                className="w-6 h-6 object-contain"
              />
            </div>
            <div className="min-w-0">
              <h4 className="font-bold text-xs sm:text-sm leading-tight text-white">Instalar PGastos</h4>
              <p className="text-[11px] text-stone-400 leading-tight truncate">Acceso rápido y modo offline</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={handleInstallClick}
              className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-md active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring flex items-center gap-1.5 cursor-pointer"
            >
              <Download className="w-3.5 h-3.5" />
              Instalar
            </button>
            <button
              onClick={handleDismiss}
              className="p-1.5 text-stone-400 hover:text-white rounded-lg hover:bg-stone-800 transition-colors active:scale-90 cursor-pointer"
              title="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal de instrucciones para cuando no hay prompt nativo automático */}
      {showInstructions && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/70 backdrop-blur-sm p-4"
          onClick={() => setShowInstructions(false)}
        >
          <div 
            className="w-full max-w-md bg-white rounded-3xl p-6 sm:p-7 text-stone-900 space-y-4 animate-spring-in border border-stone-200 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-black text-lg text-stone-900">
                  {isIOS ? 'Instalar en tu iPhone / iPad' : 'Instalar en tu navegador'}
                </h3>
                <p className="text-xs text-stone-500 font-medium">Sigue estos sencillos pasos:</p>
              </div>
              <button 
                onClick={() => setShowInstructions(false)}
                className="p-1.5 rounded-lg bg-stone-100 text-stone-500 hover:text-stone-800 transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {isIOS ? (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3 p-3 bg-stone-50 border border-stone-200 rounded-2xl">
                  <div className="p-2 bg-stone-900 text-white rounded-xl">
                    <Share className="w-5 h-5 text-indigo-400" />
                  </div>
                  <p className="text-xs font-bold text-stone-800">1. Toca el botón "Compartir" en Safari (barra inferior)</p>
                </div>

                <div className="flex items-center gap-3 p-3 bg-stone-50 border border-stone-200 rounded-2xl">
                  <div className="p-2 bg-stone-900 text-white rounded-xl">
                    <Download className="w-5 h-5 text-indigo-400" />
                  </div>
                  <p className="text-xs font-bold text-stone-800">2. Desliza hacia abajo y elige "Agregar a inicio"</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3 p-3 bg-stone-50 border border-stone-200 rounded-2xl">
                  <div className="p-2 bg-stone-900 text-white rounded-xl">
                    <Laptop className="w-5 h-5 text-indigo-400" />
                  </div>
                  <p className="text-xs font-bold text-stone-800">1. Busca el ícono de instalación (⊞ o flecha) en la barra de direcciones superior</p>
                </div>

                <div className="flex items-center gap-3 p-3 bg-stone-50 border border-stone-200 rounded-2xl">
                  <div className="p-2 bg-stone-900 text-white rounded-xl">
                    <Check className="w-5 h-5 text-emerald-400" />
                  </div>
                  <p className="text-xs font-bold text-stone-800">2. Haz clic en "Instalar PGastos" para abrir como aplicación</p>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowInstructions(false)}
              className="w-full py-3 bg-stone-900 hover:bg-stone-800 text-white font-bold text-sm rounded-xl active:scale-95 transition-all duration-150 ease-spring cursor-pointer"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallBanner;
